/**
 * Alta de sucursales (restaurante o tienda). Sólo la ve el dueño.
 *
 * Al crear una, el token de sesión viejo no la trae todavía —viaja firmada
 * dentro del JWT—, así que hay que refrescarlo antes de que la nueva
 * sucursal aparezca en el resto de la app.
 */
import { useState } from 'react';
import { api } from '../lib/api';
import { sesion, type Usuario } from '../lib/sesion';

interface Sucursal {
  id: number;
  nombre: string;
  tipo: 'restaurante' | 'abarrotes';
  meta_minutos: number;
}

interface Sesion {
  token: string;
  usuario: Usuario;
}

export function Sucursales({ sucursales }: { sucursales: Sucursal[] }) {
  const [mostrarForm, setMostrarForm] = useState(false);
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<'restaurante' | 'abarrotes'>('restaurante');
  const [direccion, setDireccion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) return;

    try {
      setGuardando(true);
      setError(null);
      await api('/sucursales', {
        method: 'POST',
        body: {
          nombre: nombre.trim(),
          tipo,
          direccion: direccion.trim() || undefined,
          telefono: telefono.trim() || undefined,
        },
      });

      // El token viejo no incluye la sucursal recién creada: hay que
      // reemitirlo antes de que el selector y las demás pantallas la vean.
      const refrescada = await api<Sesion>('/auth/refrescar', { method: 'POST' });
      sesion.abrir(refrescada.token, refrescada.usuario);

      setNombre('');
      setDireccion('');
      setTelefono('');
      setTipo('restaurante');
      setMostrarForm(false);
    } catch (err: any) {
      setError(err?.message || 'No se pudo crear la sucursal.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Sucursales</h1>
          <p className="text-sm text-salvia">Restaurantes y tiendas dados de alta en el sistema</p>
        </div>
        <button
          onClick={() => setMostrarForm(!mostrarForm)}
          className="btn-principal self-start text-sm py-2 px-4"
        >
          {mostrarForm ? 'Cancelar' : '+ Nueva Sucursal'}
        </button>
      </div>

      {mostrarForm && (
        <form onSubmit={crear} className="mb-6 rounded-2xl border border-nopal/30 bg-white p-5 shadow-sm">
          <h3 className="font-titulo text-base font-bold text-tinta mb-4">Dar de alta sucursal</h3>
          {error && <p className="mb-3 text-sm font-semibold text-barro-oscuro">{error}</p>}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Nombre *</label>
              <input
                type="text"
                required
                placeholder="Ej. Ericmar Centro"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="campo text-base"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Tipo *</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as 'restaurante' | 'abarrotes')}
                className="campo text-base"
              >
                <option value="restaurante">Restaurante</option>
                <option value="abarrotes">Abarrotes</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Dirección (opcional)</label>
              <input
                type="text"
                placeholder="Calle, número, colonia"
                value={direccion}
                onChange={(e) => setDireccion(e.target.value)}
                className="campo text-base"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Teléfono (opcional)</label>
              <input
                type="text"
                placeholder="10 dígitos"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                className="campo text-base"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button disabled={guardando} type="submit" className="btn-principal text-sm py-2 px-6">
              {guardando ? 'Guardando…' : 'Guardar Sucursal'}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {sucursales.map((s) => (
          <div
            key={s.id}
            className="flex items-center justify-between rounded-2xl border border-salvia/20 bg-white p-4 shadow-sm"
          >
            <div>
              <p className="font-titulo text-base font-bold text-tinta">{s.nombre}</p>
              <p className="text-xs capitalize text-salvia">{s.tipo}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
